import React, {
    createContext,
    useReducer,
    PropsWithChildren,
    Dispatch,
} from "react";

import { IntegrationsPayload } from "./Integrations";

export enum ActionType {
    LOGIN = "login",
    UPDATE_USER = "update",
    LOGOUT = "logout",
}

export interface User {
    id: string;
    apiKey?: string; // Optional - only present if user has subscription or unlimited access
    profilePicture?: string;
}

export interface LoginPayload {
    user: User;
    token: string;
    integrations: IntegrationsPayload;
}
export interface GoogleOAuthPayload {
    url: string;
}

interface LoginAction {
    type: ActionType.LOGIN;
    payload: LoginPayload;
}

interface UpdateUserAction {
    type: ActionType.UPDATE_USER;
    user: User;
}

interface LogoutAction {
    type: ActionType.LOGOUT;
}

type Action = LoginAction | UpdateUserAction | LogoutAction;

export interface State {
    user?: User;
    token?: string;
}

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case ActionType.LOGIN:
            // Only store user data (API key will only be included if user has access)
            localStorage.setItem("user", JSON.stringify(action.payload.user));
            localStorage.setItem("token", action.payload.token);

            return {
                ...state,
                ...action.payload,
            };

        case ActionType.UPDATE_USER:
            // Merge user data - if API key is not in the update, remove it
            // This ensures API key is cleared when user loses access
            const updatedUser = {
                ...state.user,
                ...action.user,
            };
            // If API key is not in the update (user doesn't have access), remove it
            if (!("apiKey" in action.user)) {
                delete updatedUser.apiKey;
            }
            localStorage.setItem("user", JSON.stringify(updatedUser));

            return {
                ...state,
                user: updatedUser,
            };

        case ActionType.LOGOUT:
            localStorage.removeItem("user");
            localStorage.removeItem("token");
            localStorage.removeItem("googleDriveIntegration");
            localStorage.removeItem("googleSpreadsheetAndSheet");
            return {};

        default:
            return state;
    }
};

export const Context = createContext<{
    state: State;
    dispatch: Dispatch<Action>;
}>({
    state: {},
    dispatch: () => {},
});

export const Store = ({ children }: PropsWithChildren<{}>) => {
    const existingUser: User = JSON.parse(
        localStorage.getItem("user") || "null",
    );
    const existingToken = localStorage.getItem("token");

    const [state, dispatch] = useReducer(reducer, {
        user: existingUser ?? undefined,
        token: existingToken ?? undefined,
    });

    return (
        <Context.Provider value={{ state, dispatch }}>
            {children}
        </Context.Provider>
    );
};
